import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "./Instances";
import { setUser } from "../features/authSlice";
import { logout } from "../features/authSlice";
import { addAdmission } from "../features/admissionsSlice";
import { clearUserData, setUserData } from "../features/userSlice";
import { addStudentToDynamic, setTabData } from "../features/dynamicSlice";
import { addCourseStates, updatedCourseStates } from "../features/dataSlice";
import { handleApiErrorWithLogout } from "../utils/apiHelpers";

export const LoginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/auth/login",
        {
          email,
          password,
        },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        const { token, role, user } = response.data;
        // Dispatch the user data to the Redux store
        dispatch(setUser({ token, role }));
        dispatch(setUserData(user));

        // Return the role for further handling (e.g., navigation)
        return { role };
      }
    } catch (error) {
      console.error(
        "Login failed:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message ||
          "Invalid credentials or something went wrong."
      );
    }
  }
);

export const LogoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("User is not logged in.");
      }

      const response = await axiosInstance.post(
        "/auth/logout",
        {}, // Empty body if needed
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        dispatch(logout()); // Clear Redux state
        dispatch(clearUserData());
        return { success: true, message: "Logout successful." };
      } else {
        throw new Error("Logout failed on the server.");
      }
    } catch (error) {
      console.error("LogoutUser Error:", error.response?.data || error.message);
      dispatch(logout()); // Clear Redux state
      dispatch(clearUserData());
      return rejectWithValue(
        error.response?.data?.message || "Failed to logout. Please try again."
      );
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/forgot-password", {
        email,
      });
      return response.data;
    } catch (error) {
      // Handle both Axios errors and server responses
      if (error.response && error.response.data) {
        return rejectWithValue(
          error.response.data.msg || "Something went wrong"
        );
      }
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/reset-password", {
        token,
        newPassword,
      });
      return response.data; // Expected: { msg: "Password successfully reset" }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(
          error.response.data.msg || "Something went wrong"
        );
      }
      return rejectWithValue(error.message);
    }
  }
);

export const createAdmission = createAsyncThunk(
  "admissions",
  async (formData, { dispatch, getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post("/admission", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // Important for file uploads
        },
      });

      return response.status === 201 || response.status === 200
        ? response.data.data
        : null;
    } catch (error) {
      console.error(
        "Admission creation failed:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to create admission or something went wrong."
      );
    }
  }
);

export const fetchBranches = createAsyncThunk(
  "data/fetchBranches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/branches");
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue("Unexpected response status");
      }
    } catch (error) {
      console.error(
        "Error in fetching branches:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch branches."
      );
    }
  }
);

export const fetchCourses = createAsyncThunk(
  "data/fetchCourses", // ✅ Updated slice name from 'auth' to 'data' if relevant
  async (_, { rejectWithValue }) => {
    // ✅ Corrected the parameter structure
    try {
      const response = await axiosInstance.get("/courses");

      if (response.status === 200) {
        return response.data; // ✅ Return data correctly
      } else {
        return rejectWithValue("Unexpected response from server"); // ✅ Handle non-200 responses
      }
    } catch (error) {
      console.error(
        "Error fetching courses:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message ||
          "Invalid session or something went wrong."
      );
    }
  }
);

export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/auth/verifyuser", {
        withCredentials: true,
      });
      const { user } = res;
      if (user) {
        dispatch(setUserData(user));
      }
      return res.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg || "Failed to fetch user");
    }
  }
);

export const fetchDynamicData = createAsyncThunk(
  "dynamic/fetchDynamicData",
  async (
    { url, method, body = {}, params = {}, tab },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const token = getState().auth?.token; // ✅ Correct way to access state in thunks

      const response = await axiosInstance({
        url,
        method,
        data: body, // ✅ Axios uses `data` for request body, not `body`
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        dispatch(
          setTabData({
            tab,
            data: response.data.items,
          })
        );

        return {
          tab,
          data: response.data,
        };
      } else {
        return rejectWithValue({
          tab,
          error: "Unexpected response status",
        });
      }
    } catch (error) {
      console.error(`Error fetching data for tab: ${tab}`, error);

      const handledError = await handleApiErrorWithLogout(
        error,
        dispatch,
        rejectWithValue
      );
      return rejectWithValue({
        tab,
        error: handledError?.payload || "Unexpected error in dynamic fetch",
      });
    }
  }
);

export const registerUser = createAsyncThunk(
  "register/user",
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/auth/register", userData, {
        // headers: {
        //   "Content-Type": "multipart/form-data",
        // },
      });

      if (response.status === 201 || response.status === 200) {
        const user = response.data;
        // Push to dynamic.students.data
        dispatch(addStudentToDynamic(userData)); // ⬅️ this updates dynamic slice

        return user;
      }
    } catch (error) {
      console.error("User registration failed:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to register user."
      );
    }
  }
);

export const addCourse = createAsyncThunk(
  "courses/add",
  async (formData, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token; // if stored in redux
      const response = await axiosInstance.post("/courses", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // Important for file uploads
        },
      });
      return response.status === 201 || response.status === 200
        ? response.data.course
        : null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add course"
      );
    }
  }
);

export const updateCourse = createAsyncThunk(
  "courses/update",
  async (formData, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token; // if stored in redux
      const response = await axiosInstance.put(
        `/courses/${formData.get("id")}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
        }
      );
      return response.status === 201 || response.status === 200
        ? response.data
        : null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course"
      );
    }
  }
);

export const deleteCourse = createAsyncThunk(
  "courses/delete",
  async (_id, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const response = await axiosInstance.delete(`/courses/${_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        return _id; // Return the deleted course ID so reducer can use it
      }

      return rejectWithValue("Failed to delete course");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error deleting course"
      );
    }
  }
);

export const fetchAdmissions = createAsyncThunk(
  "data/fetchAdmissions", // ✅ Updated slice name from 'auth' to 'data' if relevant
  async (_, { getState, dispatch, rejectWithValue }) => {
    // ✅ Corrected the parameter structure
    try {
      const token = getState().auth.token;
      const response = await axiosInstance.post(
        "/admissions",
        {}, // Empty body if needed
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 || response.status === 201) {
        return response.data; // ✅ Return data correctly
      } else {
        return rejectWithValue("Unexpected response from server"); // ✅ Handle non-200 responses
      }
    } catch (error) {
      console.error(
        "Error fetching Admissions Requests:",
        error.response?.data?.message || error.message
      );
      return await handleApiErrorWithLogout(error, dispatch, rejectWithValue);
      // if (axiosInstance.isAxiosError(error)) {
      //   // Server responded with status
      //   if (error.response) {
      //     return rejectWithValue(error.response.data);
      //   }
      //   // No response from server
      //   else if (error.request) {
      //     return rejectWithValue({ message: 'No response from server. Check your network.' });
      //   }
      //   // Something else went wrong in setting up the request
      //   else {
      //     return rejectWithValue({ message: error.message || 'Axios error occurred' });
      //   }
      // } else {
      //   // Non-Axios error (e.g. programming bug)
      //   return rejectWithValue({ message: 'Unexpected error occurred' });
      // }
      // return rejectWithValue(
      //   error.response?.data?.message ||
      //     "Invalid session or something went wrong."
      // );
    }
  }
);

export const processAdmission = createAsyncThunk(
  "admissions/processAdmission",
  async (formData, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const response = await axiosInstance.post(`/processadmission`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return {
        status: response.status,
        data: response.data, // 👈 entire response.data object jisme email, password, message hoga
      };
    } catch (error) {
      // Enhanced error handling
      if (error.response && error.response.status === 409) {
        return rejectWithValue({ status: 409, data: error.response.data });
      }
      if (axiosInstance.isAxiosError(error)) {
        // Server responded with status
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
        // No response from server
        else if (error.request) {
          return rejectWithValue({
            message: "No response from server. Check your network.",
          });
        }
        // Something else went wrong in setting up the request
        else {
          return rejectWithValue({
            message: error.message || "Axios error occurred",
          });
        }
      } else {
        // Non-Axios error (e.g. programming bug)
        return rejectWithValue({ message: "Unexpected error occurred" });
      }
    }
  }
);

export const getProjectedRevenue = createAsyncThunk(
  "dashboard/projectedRevenue",
  async (
    { tab = "projectedRevenue", filters },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post(
        "/dashboard/projectedrevenue",
        filters,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Backend response mein totalProjectedRevenue key h, use return kar rahe hain
      return {
        tab,
        data: response.data, // pura data jisme totalProjectedRevenue aur branches ho sakte hain
      };
    } catch (error) {
      console.error("Error fetching projected revenue:", error);

      if (error.response) {
        // Server ne response bheja error ke sath
        return rejectWithValue({
          status: error.response.status,
          data: error.response.data,
          tab,
        });
      } else if (error.request) {
        // Request bani, par server ne respond nahi kiya
        return rejectWithValue({
          message: "No response from server. Check your network connection.",
          tab,
        });
      } else {
        // Request set karte waqt koi aur error
        return rejectWithValue({
          message: error.message || "An unexpected error occurred.",
          tab,
        });
      }
    }
  }
);

export const getLatestAdmissionOrders = createAsyncThunk(
  "dashboard/latestAdmissionOrders",
  async (
    { tab = "latestAdmissionOrders", filters },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post(
        "/dashboard/latestadmissionorders",
        filters,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return {
        tab,
        data: response.data, // contains totalLatestOrders and branches[]
      };
    } catch (error) {
      console.error("Error fetching latest admission orders:", error);

      if (error.response) {
        return rejectWithValue({
          status: error.response.status,
          data: error.response.data,
          tab,
        });
      } else if (error.request) {
        return rejectWithValue({
          message: "No response from server. Check your network connection.",
          tab,
        });
      } else {
        return rejectWithValue({
          message: error.message || "An unexpected error occurred.",
          tab,
        });
      }
    }
  }
);

export const getTopCourses = createAsyncThunk(
  "dashboard/topCourses",
  async ({ tab = "topCourses", filters }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post(
        "/dashboard/topcourses",
        filters,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return {
        tab,
        data: response.data, // contains aggregateTopCourse and branches[]
      };
    } catch (error) {
      console.error("Error fetching top courses:", error);

      if (error.response) {
        return rejectWithValue({
          status: error.response.status,
          data: error.response.data,
          tab,
        });
      } else if (error.request) {
        return rejectWithValue({
          message: "No response from server. Check your network connection.",
          tab,
        });
      } else {
        return rejectWithValue({
          message: error.message || "An unexpected error occurred.",
          tab,
        });
      }
    }
  }
);

// for showing on dashboard in table
export const getAdmissionsList = createAsyncThunk(
  "admissions/fetchList",
  async (
    { tab = "latestAdmissionList", filters },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = getState().auth.token;

      const newFilters = { ...filters, limit: 10 };

      const response = await axiosInstance.post("/admissions", newFilters, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return {
        tab,
        data: response.data, // admissions data
      };
    } catch (error) {
      console.error("Error fetching admissions list:", error);

      if (error.response) {
        return rejectWithValue({
          status: error.response.status,
          data: error.response.data,
          tab,
        });
      } else if (error.request) {
        return rejectWithValue({
          message: "No response from server. Check your network connection.",
          tab,
        });
      } else {
        return rejectWithValue({
          message: error.message || "An unexpected error occurred.",
          tab,
        });
      }
    }
  }
);

export const getBranchWiseDailyComparisonData = createAsyncThunk(
  "dashboard/getBranchWiseDailyComparisonData",
  async (
    { tab = "branchWiseComparison", filters },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post(
        "/dashboard/getbranchwisedailycopmparison",
        filters,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return {
        tab,
        data: response.data, // expected: { success: true, data: [...] }
      };
    } catch (error) {
      console.error("Error fetching branch-wise daily comparison:", error);

      if (error.response) {
        return rejectWithValue({
          status: error.response.status,
          data: error.response.data,
          tab,
        });
      } else if (error.request) {
        return rejectWithValue({
          message: "No response from server. Check your network connection.",
          tab,
        });
      } else {
        return rejectWithValue({
          message: error.message || "An unexpected error occurred.",
          tab,
        });
      }
    }
  }
);

export const uploadDocument = createAsyncThunk(
  "documents/uploadDocument",
  async (formData, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post(`/documentuploads`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // फ़ाइल अपलोड के लिए ज़रूरी है
        },
      });

      return {
        status: response.status,
        data: response.data, // इसमें success message या uploaded document info हो सकती है
      };
    } catch (error) {
      if (error.response && error.response.status === 409) {
        return rejectWithValue({ status: 409, data: error.response.data });
      }

      if (axiosInstance.isAxiosError(error)) {
        if (error.response) {
          return rejectWithValue({
            status: error.response.status,
            message: error.response.data?.message || "Upload failed",
          });
        } else if (error.request) {
          return rejectWithValue({
            message: "No response from server. Check your network.",
          });
        } else {
          return rejectWithValue({
            message: error.message || "Axios error occurred",
          });
        }
      } else {
        return rejectWithValue({ message: "Unexpected error occurred" });
      }
    }
  }
);

export const fetchStudentDocuments = createAsyncThunk(
  "data/fetchStudentDocuments",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.get(
        "/getuploads",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        return response.data; // Should contain `documents` array
      } else {
        return rejectWithValue(
          "Unexpected response from server while fetching documents."
        );
      }
    } catch (error) {
      console.error(
        "Error fetching student documents:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch documents. Please check your session or try again."
      );
    }
  }
);

export const deleteDocument = createAsyncThunk(
  "data/deleteDocument",
  async (documentId, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.delete(
        `/deletedocument/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        return {
          id: documentId,
          message: response.data.message,
        };
      } else {
        return rejectWithValue("Unexpected response while deleting document.");
      }
    } catch (error) {
      console.error(
        "Delete document error:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete document."
      );
    }
  }
);

export const addBranch = createAsyncThunk(
  "branches/add",
  async (formData, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.post("/branches", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 201 || response.status === 200) {
        return response.data.branch;
      } else {
        return rejectWithValue("Branch creation failed with unexpected status");
      }
    } catch (error) {
      console.error("Error in addBranch:", error);

      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Something went wrong while adding branch"
      );
    }
  }
);

export const updateBranch = createAsyncThunk(
  "branches/update",
  async (formData, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;

      const response = await axiosInstance.put(
        `/branches/${formData._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        return response.data.data; // as per controller, updated branch is returned in `data`
      } else {
        return rejectWithValue("Unexpected server response during update");
      }
    } catch (error) {
      console.error("Error in updateBranch:", error);

      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Something went wrong while updating branch"
      );
    }
  }
);

export const deleteBranch = createAsyncThunk(
  "branches/delete", // Action type for the delete branch operation
  async (_id, { getState, rejectWithValue }) => {
    try {
      // Get the token from the auth state
      const token = getState().auth.token;

      // Make the DELETE API call to remove the branch
      const response = await axiosInstance.delete(`/branches/${_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // If the response is successful (status 200 or 201), return the branch ID
      if (response.status === 200 || response.status === 201) {
        return _id; // Return the deleted branch ID so the reducer can handle it
      }

      // Reject with a specific error message if the deletion fails
      return rejectWithValue("Failed to delete branch");
    } catch (error) {
      // Return the error message in case of failure
      return rejectWithValue(
        error.response?.data?.message || "Error deleting branch"
      );
    }
  }
);

export const fetchUserProfiles = createAsyncThunk(
  "data/fetchUserProfiles", // Updated slice name
  async ({ filters, id }, { rejectWithValue, getState }) => {
    try {
      // Build the request body based on the user's role
      const body = {
        filters,
      };

      // Make API call to fetch profiles
      const response = await axiosInstance.post("/user/profiles", body, {
        headers: {
          Authorization: `Bearer ${getState().auth.token}`, // Add token for authentication
        },
      });

      if (response.status === 200) {
        return response.data; // Return the profiles data
      } else {
        return rejectWithValue("Unexpected response from server"); // Handle unexpected responses
      }
    } catch (error) {
      console.error(
        "Error fetching user profiles:",
        error.response?.data?.message || error.message
      );
      return rejectWithValue(
        error.response?.data?.message ||
          "Something went wrong while fetching profiles."
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async ({ id, formData }, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const response = await axiosInstance.put(`/user/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // required if sending files like profilePic
        },
      });

      // Update Redux state
      dispatch(setUserData(response.data.user));

      return {
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      if (error.response && error.response.status === 409) {
        return rejectWithValue({ status: 409, data: error.response.data });
      }

      if (axiosInstance.isAxiosError(error)) {
        if (error.response) {
          return rejectWithValue(error.response.data);
        } else if (error.request) {
          return rejectWithValue({
            message: "No response from server. Check your network.",
          });
        } else {
          return rejectWithValue({
            message: error.message || "Axios error occurred",
          });
        }
      } else {
        return rejectWithValue({ message: "Unexpected error occurred" });
      }
    }
  }
);
